import * as React from "react";

import { cn } from "@/lib/utils";
import { Badge, type BadgeProps } from "@/components/ui/badge";

type StatusVariant = NonNullable<BadgeProps["variant"]>;

/**
 * Lifecycle "tone" — the §10 status axis. One source of truth so every badge
 * and dot across the ~35 entities renders identically.
 *
 * - `neutral`  → draft / proposed / pending / queued       (grey dot)
 * - `success`  → active / done / approved / passed          (green dot)
 * - `running`  → running / in_flight / streaming            (accent dot, pulses)
 * - `warning`  → blocked / needs_review / qualifies          (amber dot)
 * - `danger`   → failed / rejected / contradicts / killed    (red dot)
 * - `archived` → archived / deprecated / superseded          (faded grey)
 */
export type StatusTone =
  | "neutral"
  | "success"
  | "running"
  | "warning"
  | "danger"
  | "archived";

const STATUS_TONE: Record<string, StatusTone> = {
  // positive / terminal-good
  active: "success",
  published: "success",
  done: "success",
  completed: "success",
  approved: "success",
  success: "success",
  processed: "success",
  passed: "success",

  // in-flight (accent, pulsing)
  in_progress: "running",
  in_flight: "running",
  running: "running",
  processing: "running",
  streaming: "running",
  requested: "running",

  // neutral / not-yet-started
  review: "neutral",
  queued: "neutral",
  new: "neutral",
  draft: "neutral",
  proposed: "neutral",
  pending: "neutral",

  // attention / warning
  blocked: "warning",
  needs_review: "warning",
  review_pending: "warning",
  qualifies: "warning",
  inactive: "warning",
  disabled: "warning",
  skipped: "warning",
  suspended: "warning",

  // negative
  failed: "danger",
  error: "danger",
  rejected: "danger",
  cancelled: "danger",
  killed: "danger",
  contradicts: "danger",
  non_replayable: "danger",

  // faded / out-of-lifecycle
  archived: "archived",
  deprecated: "archived",
  superseded: "archived",

  // priorities (reuse the same dot palette so lists read at a glance)
  low: "archived",
  medium: "neutral",
  high: "warning",
  urgent: "danger",
};

const TONE_BADGE_VARIANT: Record<StatusTone, StatusVariant> = {
  neutral: "secondary",
  success: "success",
  running: "secondary",
  warning: "warning",
  danger: "destructive",
  archived: "outline",
};

const TONE_DOT_CLASS: Record<StatusTone, string> = {
  neutral: "bg-muted-foreground",
  success: "bg-success",
  running: "bg-running animate-status-pulse",
  warning: "bg-warning",
  danger: "bg-destructive",
  archived: "bg-muted-foreground/60",
};

function toneOf(status: string | null | undefined): StatusTone {
  const key = (status ?? "").toString().toLowerCase();
  return STATUS_TONE[key] ?? "neutral";
}

function humanize(status: string): string {
  return status
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export interface StatusDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Raw status / priority string from a record. */
  status: string | null | undefined;
  /** Explicit tone override; otherwise derived from `status`. */
  tone?: StatusTone;
}

/**
 * A 6–8px status dot (§10). Status colour is carried here; the in-flight
 * `running` tone pulses (static fallback under `prefers-reduced-motion`, §8).
 * Used standalone in dense tables and as the leading mark inside StatusBadge.
 */
export function StatusDot({ status, tone, className, ...props }: StatusDotProps) {
  const resolved = tone ?? toneOf(status);
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block h-2 w-2 shrink-0 rounded-full",
        TONE_DOT_CLASS[resolved],
        className,
      )}
      {...props}
    />
  );
}

export interface StatusBadgeProps
  extends Omit<BadgeProps, "variant" | "children"> {
  /** Raw status / priority string from a record. */
  status: string | null | undefined;
  /** Optional explicit badge-variant override; otherwise derived from `status`. */
  variant?: StatusVariant;
  /** Custom display label; defaults to the humanized status. */
  label?: string;
}

/**
 * §10 status pill: a calm `bg-muted` chip with a **leading tinted dot + the
 * state word** — never a saturated filled pill. Word always present so status
 * is never conveyed by colour alone (a11y, §9). Unknown values still render.
 */
export function StatusBadge({
  status,
  variant,
  label,
  className,
  ...props
}: StatusBadgeProps) {
  const raw = (status ?? "").toString();
  const tone = toneOf(status);
  const resolved = variant ?? TONE_BADGE_VARIANT[tone];
  const text = label ?? (raw ? humanize(raw) : "—");
  return (
    <Badge variant={resolved} className={className} {...props}>
      <StatusDot status={status} tone={tone} />
      {text}
    </Badge>
  );
}

export default StatusBadge;
